import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useAuth } from '@/lib/contexts/auth-context';
import { useTranslation } from 'react-i18next';

export default function VerifyEmail() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { refreshUser } = useAuth();
    const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const verifyEmail = async () => {
            try {
                // Récupérer les paramètres de l'URL
                const id = searchParams.get('id');
                const hash = searchParams.get('hash');
                const expires = searchParams.get('expires');
                const signature = searchParams.get('signature');

                // Vérifier que tous les paramètres sont présents
                if (!id || !hash || !expires || !signature) {
                    setStatus('error');
                    setErrorMessage('Lien de vérification invalide ou incomplet.');
                    return;
                }

                // Appeler l'API pour vérifier l'email
                await horodatyApi.verifyEmail({ id, hash, expires, signature });

                // Rafraîchir les infos utilisateur pour mettre à jour le statut de vérification
                await refreshUser();

                // Indiquer le succès
                setStatus('success');

                // Rediriger vers le dashboard après 2 secondes
                setTimeout(() => {
                    navigate('/dashboard');
                }, 2000);
            } catch (error) {
                console.error('Email verification error:', error);
                setStatus('error');

                // Essayer d'extraire un message d'erreur plus explicite
                const message =
                    error?.response?.data?.message ||
                    error?.message ||
                    t('verifyemail:impossibleDeVerifierVotreAdresseEmailLeLien');

                setErrorMessage(message);
            }
        };

        verifyEmail();
    }, [searchParams, navigate, refreshUser]);

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4">
            <div className="max-w-md w-full bg-card rounded-2xl shadow-lg p-8 text-center">
                {status === 'loading' && (
                    <>
                        <Loader2 className="w-16 h-16 text-secondary mx-auto mb-4 animate-spin" />
                        <h1 className="text-2xl font-bold mb-2">{t('verifyemail:verificationEnCours')}</h1>
                        <p className="text-muted-foreground">
                            {t('verifyemail:veuillezPatienterPendantQueNousVerifionsVotreAdr')}
                        </p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-10 h-10 text-success" />
                        </div>
                        <h1 className="text-2xl font-bold mb-2">{t('verifyemail:emailVerifie')}</h1>
                        <p className="text-muted-foreground mb-4">
                            {t('verifyemail:votreAdresseEmailAEteVerifieeAvecSucces')}
                        </p>
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>{t('verifyemail:redirectionEnCours')}</span>
                        </div>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <XCircle className="w-10 h-10 text-destructive" />
                        </div>
                        <h1 className="text-2xl font-bold mb-2">{t('verifyemail:erreurDeVerification')}</h1>
                        <p className="text-muted-foreground mb-6">{errorMessage}</p>
                        <Button
                            variant="solid"
                            color="secondary"
                            onClick={() => navigate('/dashboard')}
                            className="px-6 py-3 rounded-xl font-medium hover:opacity-90 transition-opacity"
                        >
                            {t('verifyemail:retourAuTableauDeBord')}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}
